import React, { useState, useEffect, useMemo } from 'react';
import { ScrollView, View, Text, TextInput, StyleSheet, TouchableOpacity, Image, ActivityIndicator } from 'react-native';
import { useNavigate } from 'react-router-dom';
import { useSeries, useContinueWatching, useSearchSeries, useCategories } from '../../hooks/useSeries';
import { useIncrementSeriesViews } from '../../hooks/useSeriesViews';
import { useSubscription } from '../../hooks/useSubscription';
import { BuyButton } from '../../components/BuyButton';
import { isPaymentFirstFlow } from '../../config/authFlow';
import { HeroSection } from '../../components/HeroSection';
import { ContinueWatchingRow } from '../../components/ContinueWatchingRow';
import { SeriesGrid } from '../../components/SeriesGrid';
import { FeaturedBanners } from '../../components/FeaturedBanners';
import { RequestSeriesModal } from '../../components/RequestSeriesModal';
import { NETFLIX_COLORS, NETFLIX_SPACING } from '../../theme/netflixTheme';

export default function HomeScreen(): JSX.Element {
  const navigate = useNavigate();
  const [inputValue, setInputValue] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isRequestSeriesModalOpen, setIsRequestSeriesModalOpen] = useState(false);
  const { refetch: refetchSubscription } = useSubscription();

  // Carregar assinatura UMA VEZ na entrada
  useEffect(() => {
    console.log('🏠 [HomeScreen] Carregando assinatura...');
    refetchSubscription();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(inputValue);
    }, 300);

    return () => clearTimeout(timer);
  }, [inputValue]);

  const trendingParams = useMemo(() => ({ limit: 24, sort: 'trending' as const, page: 1 }), []);
  const recentParams = useMemo(() => ({ limit: 24, sort: 'recent' as const, page: 1 }), []);
  const ratingParams = useMemo(() => ({ limit: 24, sort: 'rating' as const, page: 1 }), []);
  const categoryParams = useMemo(() => ({ limit: 24, category: selectedCategory, page: currentPage }), [selectedCategory, currentPage]);
  const searchParams = useMemo(() => ({ limit: 20, page: 1 }), []);

  const { data: trendingData, isLoading: trendingLoading } = useSeries(trendingParams);
  const { data: recentData, isLoading: recentLoading } = useSeries(recentParams);
  const { data: ratingData, isLoading: ratingLoading } = useSeries(ratingParams);
  const { data: categoryData, isLoading: categoryLoading } = useSeries(selectedCategory ? categoryParams : undefined);

  const { data: historyData } = useContinueWatching(6);
  const { data: searchData, isLoading: searchLoading } = useSearchSeries(debouncedQuery, searchParams);
  const { data: categoriesData } = useCategories();

  const allTrending = useMemo(() => trendingData?.data || [], [trendingData]);
  const allRecent = useMemo(() => recentData?.data || [], [recentData]);
  const allRating = useMemo(() => ratingData?.data || [], [ratingData]);
  const categoryList = useMemo(() => categoryData?.data || [], [categoryData]);
  const history = useMemo(() => historyData || [], [historyData]);
  const searchResults = useMemo(() => searchData?.data || [], [searchData]);
  const categories = useMemo(() => categoriesData || [], [categoriesData]);

  useEffect(() => {
    console.log('🎬 [HomeScreen] DEBUG - history atualizado:');
    console.log('   - historyData (raw):', historyData);
    console.log('   - history (processado):', history);
    console.log('   - history.length:', history.length);
    if (history.length > 0) {
      console.log('   - Primeiro item:', history[0]);
    }
  }, [history, historyData]);

  const featured = allTrending.find((s) => s.is_featured) || allTrending[0];
  const showSearch = debouncedQuery.length > 0;

  return (
    <>
    <ScrollView
      style={styles.container}
      scrollEventThrottle={16}
      showsVerticalScrollIndicator={false}
    >
      {/* Search Section */}
      <div style={searchWrapperStyle}>
        <View style={styles.searchSection}>
          <View style={styles.searchInputContainer}>
            <TextInput
              style={styles.searchInput}
              placeholder="Buscar séries..."
              placeholderTextColor={NETFLIX_COLORS.textTertiary}
              value={inputValue}
              onChangeText={setInputValue}
            />
          </View>
        </View>
      </div>

      {/* Main Content */}
      <div style={mainWrapperStyle}>
        {showSearch ? (
          <>
            {searchLoading ? (
              <View style={styles.centerContainer}>
                <ActivityIndicator size="large" color={NETFLIX_COLORS.primary} />
                <Text style={styles.loadingText}>Buscando...</Text>
              </View>
            ) : searchResults.length > 0 ? (
              <>
                <Text style={styles.sectionTitle}>
                  {searchResults.length} resultado(s)
                </Text>
                <div style={gridStyle}>
                  {searchResults.map((item) => (
                    <SeriesCard key={item.id} series={item} onPress={() => navigate(`/series/${item.id}`)} />
                  ))}
                </div>
              </>
            ) : (
              <View style={styles.centerContainer}>
                <Text style={styles.emptyTitle}>Nenhuma série encontrada</Text>
                <Text style={styles.emptySubtitle}>Tente outro termo de busca</Text>
              </View>
            )}
          </>
        ) : (
          <>
            {/* Featured Banners */}
            {allTrending && (
              <FeaturedBanners series={allTrending} isLoading={trendingLoading} />
            )}

            {/* Hero */}
            {featured && (
              <View style={styles.heroWrapper}>
                <HeroSection series={featured} />
              </View>
            )}

            {/* Request Series Banner */}
            <div style={requestSeriesBannerStyle}>
              <div style={requestSeriesBannerContent}>
                <div>
                  <h3 style={requestSeriesBannerTitle}>Não encontrou a série que procura? 📺</h3>
                  <p style={requestSeriesBannerText}>Nos ajude sugerindo novos conteúdos</p>
                </div>
                <TouchableOpacity
                  style={styles.requestSeriesButton}
                  onPress={() => setIsRequestSeriesModalOpen(true)}
                >
                  <Text style={styles.requestSeriesButtonText}>Solicitar Série</Text>
                </TouchableOpacity>
              </div>
            </div>

            {/* Categorias */}
            {categories.length > 0 && (
              <View style={styles.categoriesSection}>
                <Text style={styles.sectionTitle}>Categorias</Text>
                <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.categoriesScroll}>
                  {categories.map((cat) => (
                    <TouchableOpacity
                      key={cat.id}
                      style={[
                        styles.categoryTag,
                        selectedCategory === cat.id && styles.categoryTagActive,
                      ]}
                      onPress={() => {
                        setSelectedCategory(selectedCategory === cat.id ? null : cat.id);
                        setCurrentPage(1);
                      }}
                    >
                      <Text style={styles.categoryTagText}>{cat.name}</Text>
                    </TouchableOpacity>
                  ))}
                </ScrollView>
              </View>
            )}

            {/* Continuar Assistindo - Sempre visível quando há histórico */}
            {history.length > 0 && !selectedCategory && (
              <View style={styles.section}>
                <ContinueWatchingRow items={history} isLoading={false} />
              </View>
            )}

            {/* Conteúdo por categoria ou padrão */}
            {selectedCategory ? (
              <>
                <View style={styles.section}>
                  <SeriesGrid
                    title={categories.find(c => c.id === selectedCategory)?.name || 'Categoria'}
                    series={categoryList}
                    isLoading={categoryLoading}
                    horizontal={false}
                  />
                </View>
                {categoryList.length > 0 && (
                  <View style={styles.paginationContainer}>
                    <TouchableOpacity
                      style={[styles.paginationButton, currentPage === 1 && styles.paginationButtonDisabled]}
                      onPress={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                    >
                      <Text style={styles.paginationButtonText}>← Anterior</Text>
                    </TouchableOpacity>
                    <Text style={styles.paginationText}>Página {currentPage}</Text>
                    <TouchableOpacity
                      style={styles.paginationButton}
                      onPress={() => setCurrentPage(currentPage + 1)}
                    >
                      <Text style={styles.paginationButtonText}>Próxima →</Text>
                    </TouchableOpacity>
                  </View>
                )}
              </>
            ) : (
              <>
                {/* Seções padrão quando nenhuma categoria selecionada */}

                {/* Trending */}
                <View style={styles.section}>
                  <SeriesGrid
                    title="Em Alta Agora"
                    series={allTrending}
                    isLoading={trendingLoading}
                    horizontal={false}
                  />
                </View>

                {/* Lançamentos Recentes */}
                <View style={styles.section}>
                  <SeriesGrid
                    title="Lançamentos Recentes"
                    series={allRecent}
                    isLoading={recentLoading}
                    horizontal={false}
                  />
                </View>

                {/* Melhores Avaliações */}
                <View style={styles.section}>
                  <SeriesGrid
                    title="Melhores Avaliações"
                    series={allRating}
                    isLoading={ratingLoading}
                    horizontal={false}
                  />
                </View>
              </>
            )}
          </>
        )}
      </div>

      <View style={styles.bottomPadding} />
    </ScrollView>

    {/* Request Series Modal */}
    <RequestSeriesModal
      isOpen={isRequestSeriesModalOpen}
      onClose={() => setIsRequestSeriesModalOpen(false)}
    />
    </>
  );
}

interface SeriesCardProps {
  series: any;
  onPress: () => void;
}

function SeriesCard({ series, onPress }: SeriesCardProps) {
  const incrementViews = useIncrementSeriesViews();
  const navigate = useNavigate();
  const isComingSoon = series.status === 'ongoing';
  const totalViews = (series.views_real || 0) + (series.views_fake || 0);

  const handlePress = () => {
    incrementViews.mutate(series.id);
    onPress();
  };

  const handleBuyClick = () => {
    navigate('/plans');
  };

  return (
    <TouchableOpacity style={styles.seriesCard} onPress={handlePress}>
      <View style={styles.posterContainer}>
        {series.poster_url ? (
          <Image source={{ uri: series.poster_url }} style={styles.seriesPoster} />
        ) : (
          <View style={[styles.seriesPoster, styles.posterPlaceholder]}>
            <Text style={styles.posterIcon}>—</Text>
          </View>
        )}
        {isComingSoon && (
          <View style={styles.comingSoonBadge}>
            <Text style={styles.comingSoonText}>EM BREVE</Text>
          </View>
        )}
        {isPaymentFirstFlow() && (
          <View style={styles.buyButtonOverlay}>
            <BuyButton onPress={handleBuyClick} size="small" />
          </View>
        )}
      </View>
      <View style={styles.cardInfo}>
        <Text style={styles.cardTitle} numberOfLines={2}>
          {series.title}
        </Text>
        <View style={styles.cardMeta}>
          <Text style={styles.rating}>{series.rating.toFixed(1)}</Text>
          <Text style={styles.year}>{series.release_year}</Text>
        </View>
        <Text style={styles.views}>{totalViews.toLocaleString('pt-BR')} visualizações</Text>
      </View>
    </TouchableOpacity>
  );
}

const searchWrapperStyle: React.CSSProperties = {
  maxWidth: '100%',
  backgroundColor: NETFLIX_COLORS.background,
  borderBottomWidth: 0,
  padding: `${NETFLIX_SPACING.xl}px 0`,
};

const mainWrapperStyle: React.CSSProperties = {
  maxWidth: '1440px',
  margin: '0 auto',
  width: '100%',
  padding: `0 ${NETFLIX_SPACING.xl}px`,
};

const gridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
  gap: `${NETFLIX_SPACING.lg}px`,
  marginTop: NETFLIX_SPACING.xl,
};

const requestSeriesBannerStyle: React.CSSProperties = {
  background: `linear-gradient(135deg, ${NETFLIX_COLORS.primary} 0%, ${NETFLIX_COLORS.primaryHover} 100%)`,
  borderRadius: '12px',
  padding: `${NETFLIX_SPACING.lg}px`,
  marginTop: NETFLIX_SPACING.xxxl,
  marginBottom: NETFLIX_SPACING.xxxl,
};

const requestSeriesBannerContent: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: NETFLIX_SPACING.lg,
};

const requestSeriesBannerTitle: React.CSSProperties = {
  color: NETFLIX_COLORS.white,
  fontSize: '20px',
  fontWeight: '700',
  margin: '0 0 8px 0',
};

const requestSeriesBannerText: React.CSSProperties = {
  color: 'rgba(255, 255, 255, 0.9)',
  fontSize: '14px',
  margin: '0',
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: NETFLIX_COLORS.background,
  },
  searchSection: {
    maxWidth: 1440,
    marginHorizontal: 'auto',
    paddingHorizontal: NETFLIX_SPACING.xl,
  },
  searchInputContainer: {
    position: 'relative',
  },
  searchInput: {
    width: '100%',
    backgroundColor: NETFLIX_COLORS.surface,
    borderRadius: 12,
    padding: NETFLIX_SPACING.lg,
    paddingLeft: NETFLIX_SPACING.xl,
    color: NETFLIX_COLORS.textPrimary,
    fontSize: 15,
    borderWidth: 1,
    borderColor: 'transparent',
  },
  section: {
    marginTop: NETFLIX_SPACING.xxxl,
  },
  heroWrapper: {
    marginBottom: NETFLIX_SPACING.xxxl,
  },
  sectionTitle: {
    color: NETFLIX_COLORS.textPrimary,
    fontSize: 20,
    fontWeight: '700',
    marginBottom: NETFLIX_SPACING.lg,
  },
  categoryCard: {
    backgroundColor: NETFLIX_COLORS.surface,
    borderRadius: 8,
    padding: NETFLIX_SPACING.lg,
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 90,
    borderWidth: 1,
    borderColor: NETFLIX_COLORS.border,
  },
  categoryName: {
    color: NETFLIX_COLORS.textPrimary,
    fontSize: 13,
    fontWeight: '500',
    textAlign: 'center',
  },
  seriesCard: {
    width: '100%',
  },
  posterContainer: {
    position: 'relative',
  },
  seriesPoster: {
    width: '100%',
    height: 240,
    borderRadius: 8,
    backgroundColor: NETFLIX_COLORS.surface,
  },
  comingSoonBadge: {
    position: 'absolute',
    top: NETFLIX_SPACING.md,
    right: NETFLIX_SPACING.md,
    backgroundColor: '#FF6B6B',
    paddingHorizontal: NETFLIX_SPACING.md,
    paddingVertical: NETFLIX_SPACING.sm,
    borderRadius: 4,
  },
  buyButtonOverlay: {
    position: 'absolute',
    bottom: NETFLIX_SPACING.md,
    right: NETFLIX_SPACING.md,
  },
  comingSoonText: {
    color: NETFLIX_COLORS.white,
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 0.5,
  },
  posterPlaceholder: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  posterIcon: {
    fontSize: 48,
    color: NETFLIX_COLORS.textTertiary,
  },
  cardInfo: {
    paddingTop: NETFLIX_SPACING.md,
  },
  cardTitle: {
    color: NETFLIX_COLORS.textPrimary,
    fontSize: 13,
    fontWeight: '500',
    marginBottom: NETFLIX_SPACING.sm,
    lineHeight: 18,
  },
  cardMeta: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: NETFLIX_SPACING.xs,
  },
  rating: {
    color: NETFLIX_COLORS.accent,
    fontSize: 12,
    fontWeight: '600',
  },
  year: {
    color: NETFLIX_COLORS.textTertiary,
    fontSize: 11,
  },
  views: {
    color: NETFLIX_COLORS.textTertiary,
    fontSize: 10,
  },
  centerContainer: {
    minHeight: 400,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    color: NETFLIX_COLORS.textSecondary,
    fontSize: 14,
    marginTop: NETFLIX_SPACING.lg,
  },
  emptyTitle: {
    color: NETFLIX_COLORS.textPrimary,
    fontSize: 18,
    fontWeight: '600',
    marginBottom: NETFLIX_SPACING.md,
  },
  emptySubtitle: {
    color: NETFLIX_COLORS.textTertiary,
    fontSize: 14,
  },
  categoriesSection: {
    marginTop: NETFLIX_SPACING.xxxl,
    marginBottom: NETFLIX_SPACING.xxl,
  },
  categoriesScroll: {
    marginTop: NETFLIX_SPACING.lg,
  },
  categoryTag: {
    backgroundColor: NETFLIX_COLORS.surface,
    borderRadius: 20,
    paddingHorizontal: NETFLIX_SPACING.lg,
    paddingVertical: NETFLIX_SPACING.sm,
    marginRight: NETFLIX_SPACING.md,
    borderWidth: 1,
    borderColor: NETFLIX_COLORS.border,
  },
  categoryTagActive: {
    backgroundColor: NETFLIX_COLORS.primary,
    borderColor: NETFLIX_COLORS.primary,
  },
  categoryTagText: {
    color: NETFLIX_COLORS.textPrimary,
    fontSize: 13,
    fontWeight: '600',
  },
  paginationContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: NETFLIX_SPACING.lg,
    marginTop: NETFLIX_SPACING.xxxl,
    marginBottom: NETFLIX_SPACING.xxxl,
  },
  paginationButton: {
    backgroundColor: NETFLIX_COLORS.primary,
    paddingHorizontal: NETFLIX_SPACING.lg,
    paddingVertical: NETFLIX_SPACING.md,
    borderRadius: 6,
  },
  paginationButtonDisabled: {
    opacity: 0.5,
  },
  paginationButtonText: {
    color: NETFLIX_COLORS.white,
    fontSize: 14,
    fontWeight: '600',
  },
  paginationText: {
    color: NETFLIX_COLORS.textPrimary,
    fontSize: 14,
    fontWeight: '500',
  },
  bottomPadding: {
    height: 50,
  },
  requestSeriesButton: {
    backgroundColor: NETFLIX_COLORS.white,
    paddingHorizontal: NETFLIX_SPACING.lg,
    paddingVertical: NETFLIX_SPACING.md,
    borderRadius: 8,
  },
  requestSeriesButtonText: {
    color: NETFLIX_COLORS.primary,
    fontSize: 14,
    fontWeight: '700',
  },
});
